import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingCart, ArrowLeft, Plus, Minus, Package, CheckCircle2, XCircle, Truck, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../contexts/CartContext';
import Navbar from './Navbar';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  return `${BACKEND_URL}${src}`;
};

const ToolDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, totalItems, currency, setCurrency, symbol } = useCart();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [imgError, setImgError] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    setImgError(false);
    setQty(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    axios
      .get(`${API}/tools/${id}`)
      .then((res) => { if (mounted) setTool(res.data); })
      .catch(() => { if (mounted) setNotFound(true); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id]);

  // Related products from the same category (exclude current, max 4)
  useEffect(() => {
    if (!tool?.category) { setRelated([]); return; }
    let mounted = true;
    axios
      .get(`${API}/tools/`, { params: { category: tool.category } })
      .then((res) => {
        if (!mounted) return;
        const others = (res.data || []).filter((t) => t.id !== tool.id).slice(0, 4);
        setRelated(others);
      })
      .catch(() => { if (mounted) setRelated([]); });
    return () => { mounted = false; };
  }, [tool?.id, tool?.category]);

  const priceField = currency === 'eur' ? 'price_eur' : 'price_usd';

  const handleAdd = () => {
    addItem(tool, qty);
    toast.success(`${tool.name} (x${qty}) añadido al carrito`, { duration: 2000 });
  };

  const handleAddRelated = (e, item) => {
    e.stopPropagation();
    addItem(item, 1);
    toast.success(`${item.name} añadido al carrito`, { duration: 2000 });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50">
      <Navbar />
      <div className="pt-24 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="tool-detail-page">
        <div className="flex items-center justify-between mb-6">
          <button
            data-testid="detail-back-btn"
            onClick={() => navigate('/tienda')}
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 transition-colors"
          >
            <ArrowLeft size={16} /> Volver a la tienda
          </button>
          <Link to="/carrito">
            <Button data-testid="detail-cart-btn" className="bg-slate-900 hover:bg-slate-800 text-white relative" size="sm">
              <ShoppingCart size={16} className="mr-2" /> Carrito
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent mx-auto" />
            <p className="text-slate-500 mt-3">Cargando producto...</p>
          </div>
        ) : notFound || !tool ? (
          <div className="text-center py-24" data-testid="detail-not-found">
            <Package size={56} className="text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">Producto no encontrado.</p>
            <Button onClick={() => navigate('/tienda')} className="bg-red-600 hover:bg-red-700 text-white">
              Volver a la tienda
            </Button>
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Image */}
            <div className="rounded-2xl overflow-hidden bg-white shadow-md border border-slate-100">
              {tool.image && !imgError ? (
                <img
                  src={resolveImg(tool.image)}
                  alt={tool.name}
                  className="w-full aspect-square object-cover"
                  onError={() => setImgError(true)}
                  data-testid="detail-image"
                />
              ) : (
                <div className="w-full aspect-square flex flex-col items-center justify-center bg-slate-50 text-slate-300">
                  <Package size={72} />
                  <span className="text-sm mt-2">Sin imagen</span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex flex-col">
              <span className="text-xs uppercase tracking-wide text-red-600 font-bold" data-testid="detail-id">{tool.id}</span>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1" data-testid="detail-name">
                {tool.name}
              </h1>

              <div className="flex items-center gap-3 mt-4">
                <div className="flex bg-white border rounded-lg overflow-hidden">
                  <button
                    data-testid="detail-currency-usd"
                    onClick={() => setCurrency('usd')}
                    className={`px-3 py-1.5 text-xs font-semibold ${currency === 'usd' ? 'bg-red-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >USD ($)</button>
                  <button
                    data-testid="detail-currency-eur"
                    onClick={() => setCurrency('eur')}
                    className={`px-3 py-1.5 text-xs font-semibold ${currency === 'eur' ? 'bg-red-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >EUR (€)</button>
                </div>
                {tool.stock ? (
                  <span className="inline-flex items-center gap-1 text-sm text-emerald-600 font-medium"><CheckCircle2 size={16} /> En stock</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-sm text-red-500 font-medium"><XCircle size={16} /> Sin stock</span>
                )}
              </div>

              <p className="text-4xl font-bold text-slate-900 mt-5" data-testid="detail-price">
                {symbol}{tool[priceField].toFixed(2)}
                <span className="text-sm font-normal text-slate-400 ml-2">{currency.toUpperCase()}</span>
              </p>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">Descripción</h3>
                <p className="text-slate-600 leading-relaxed whitespace-pre-line" data-testid="detail-description">
                  {tool.description || 'Sin descripción disponible.'}
                </p>
              </div>

              {/* Shipping info */}
              <div className="mt-6 space-y-2 rounded-xl border border-slate-200 bg-white p-4" data-testid="detail-shipping-info">
                <div className="flex items-center gap-2 text-sm text-slate-700" data-testid="detail-delivery-estimate">
                  <Truck size={18} className="text-red-600 flex-shrink-0" />
                  <span>
                    Entrega estimada: <strong>12-20 días</strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <Globe size={18} className="text-red-600 flex-shrink-0" />
                  <span>Envío internacional · seguimiento incluido</span>
                </div>
              </div>

              {/* Quantity + Add */}
              <div className="flex items-center gap-4 mt-8">
                <div className="flex items-center border rounded-lg overflow-hidden bg-white">
                  <button
                    data-testid="detail-qty-minus"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="px-3 py-2 text-slate-600 hover:bg-slate-100"
                  ><Minus size={16} /></button>
                  <span className="px-4 py-2 font-semibold text-slate-900 min-w-[3rem] text-center" data-testid="detail-qty">{qty}</span>
                  <button
                    data-testid="detail-qty-plus"
                    onClick={() => setQty((q) => Math.min(99, q + 1))}
                    className="px-3 py-2 text-slate-600 hover:bg-slate-100"
                  ><Plus size={16} /></button>
                </div>
                <Button
                  data-testid="detail-add-btn"
                  onClick={handleAdd}
                  disabled={!tool.stock}
                  className="bg-red-600 hover:bg-red-700 text-white flex-1 py-6 text-base"
                >
                  <ShoppingCart size={18} className="mr-2" /> Añadir al carrito
                </Button>
              </div>

              <p className="text-xs text-slate-400 mt-4">
                Envío a todo el mundo. El coste de envío se cotiza tras la compra.
              </p>
            </div>
          </div>

          {related.length > 0 && (
            <section className="mt-16" data-testid="related-products">
              <h2 className="text-lg md:text-xl font-bold text-slate-900 mb-6">
                Productos <span className="text-red-600">relacionados</span>
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {related.map((item) => (
                  <div
                    key={item.id}
                    data-testid={`related-tool-${item.id}`}
                    onClick={() => navigate(`/tienda/producto/${item.id}`)}
                    className="group bg-white rounded-xl shadow-md hover:shadow-2xl transition-shadow duration-300 overflow-hidden flex flex-col cursor-pointer"
                  >
                    <div
                      className="aspect-[4/3] bg-slate-100 bg-cover bg-center transition-transform duration-300 group-hover:scale-[1.02]"
                      style={{ backgroundImage: `url(${resolveImg(item.image)})` }}
                    />
                    <div className="p-3 flex flex-col flex-1">
                      <h3 className="text-sm font-semibold text-slate-900 line-clamp-2 leading-tight min-h-[2.5rem] group-hover:text-red-600 transition-colors">
                        {item.name}
                      </h3>
                      <div className="flex items-end justify-between mt-auto pt-3">
                        <p className="text-lg font-bold text-slate-900">
                          {symbol}{item[priceField].toFixed(2)}
                        </p>
                        <Button
                          data-testid={`related-add-${item.id}`}
                          onClick={(e) => handleAddRelated(e, item)}
                          disabled={!item.stock}
                          className="bg-red-600 hover:bg-red-700 text-white"
                          size="sm"
                        >
                          <Plus size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ToolDetail;
